import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  Radio,
  RadioGroup,
  Select,
  MenuItem,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Drawer,
  Checkbox,
  Tooltip,
} from '@mui/material';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import type { AxiosError } from 'axios';
import AppLayout from '../components/AppLayout';
import { useAuth } from '../contexts/AuthContext';
import { useAgentContext } from '../contexts/AgentContext';
import { usePageAgentContext } from '../hooks/usePageAgentContext';
import { apiService } from '../services/api';
import { API_ENDPOINTS } from '../constants/api';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import AddIcon from '@mui/icons-material/Add';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import EditIcon from '@mui/icons-material/Edit';
import { formatDate as formatAppDate, formatCalendarDateIso } from '../utils/dateFormat';
import LeaveBarChart from '../components/LeaveBarChart';
import LeaveDataTable from '../components/LeaveDataTable';
import MyLeaveHelpSidebar from '../components/MyLeaveHelpSidebar';
import DocumentUploadDialog from '../components/DocumentUploadDialog';
import SupportingDocumentsTable from '../components/SupportingDocumentsTable';
import { isViewableContentType, openBlobInNewTab } from '../utils/documentViewer';
import { formatLeaveRequestComments } from '../utils/leaveRequestDisplay';
import { getApiErrorMessage } from '../utils/apiError';
import { couldNotLoad, couldNotSubmit, couldNotUpdate, couldNotCreate, couldNotProcess, couldNotWithdraw, documentCopy } from '../utils/userFacingCopy';

interface MyLeaveReportEntry {
  employeeId: string;
  employeeName: string;
  employeeNumber?: number;
  departmentName?: string;
  businessUnitName?: string;
  leaveTypeName: string;
  daysTaken: number;
  periodYear: number;
  statutoryEntitlementMet: boolean;
  fromDate?: string | null;
  toDate?: string | null;
  periodDescription?: string | null;
  leaveRequestId?: string | null;
}

interface LeaveReportResponse {
  entries: MyLeaveReportEntry[];
}

interface LeaveTypeDto {
  id: string;
  code: string;
  name: string;
  countsTowardAnnualCap: boolean;
  maxDaysPerYear?: number;
  displayOrder: number;
  isActive: boolean;
}

interface LeaveEntitlementMessageDto {
  accruedToDate: number;
  cycleEntitlement: number;
  takenInCycle: number;
  availableBalance: number;
  requestedDays: number;
  daysWouldBeAdvancedLeave: number;
  fiscalYear: number;
  appliesToThisType: boolean;
  evidenceRequired?: boolean;
  evidenceRequiredReason?: string;
  capMessage?: string;
}

interface LeaveRequestDto {
  id: string;
  requesterId: string;
  requesterName?: string;
  createdByUserId?: string | null;
  createdByName?: string | null;
  leaveTypeId: string;
  leaveTypeName?: string;
  leaveTypeCode?: string;
  fromDate: string;
  toDate: string;
  isHalfDay?: boolean;
  requestedDays: number;
  comments?: string;
  status: string;
  submittedAtUtc?: string | null;
  approvedById?: string | null;
  approvedByName?: string | null;
  approvedAtUtc?: string | null;
  approvedWithAdvancedLeave: boolean;
  evidenceRequired?: boolean;
  evidenceRequiredReason?: string;
  reasonForChange?: string | null;
  createdAtUtc: string;
}

interface LeaveRequestEvidenceDto {
  id: string;
  fileName: string;
  contentType?: string;
  fileSize: number;
  uploadedById: string;
  uploadedByName: string;
  uploadedAt: string;
  description?: string;
}

const toDateInputValue = (s: string | undefined | null) => (s && s.length >= 10 ? s.slice(0, 10) : '');

interface EditLeaveDialogProps {
  request: LeaveRequestDto | null;
  leaveTypes: LeaveTypeDto[];
  onClose: () => void;
  onSubmit: (payload: {
    leaveTypeId: string;
    fromDate: string;
    toDate: string;
    isHalfDay: boolean;
    comments?: string;
    reasonForChange?: string;
  }) => Promise<void>;
}

function EditLeaveDialog({ request, leaveTypes, onClose, onSubmit }: EditLeaveDialogProps) {
  const [leaveTypeId, setLeaveTypeId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [halfDay, setHalfDay] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const commentsRef = useRef('');
  const reasonRef = useRef('');

  useEffect(() => {
    if (request) {
      setLeaveTypeId(request.leaveTypeId);
      setFrom(toDateInputValue(request.fromDate));
      setTo(toDateInputValue(request.toDate));
      setHalfDay(request.isHalfDay ?? false);
      commentsRef.current = request.comments ?? '';
      reasonRef.current = '';
      setError('');
    }
  }, [request]);

  const today = formatCalendarDateIso(new Date());
  const requiresReason = request?.status === 'Approved' && (from <= today || to < today);

  const handleSubmit = async () => {
    if (!request || !leaveTypeId || !from || !to) return;
    const reasonVal = reasonRef.current.trim();
    if (requiresReason && !reasonVal) {
      setError('Please provide a reason for the change.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await onSubmit({
        leaveTypeId,
        fromDate: from,
        toDate: to,
        isHalfDay: halfDay,
        comments: commentsRef.current.trim() || undefined,
        reasonForChange: requiresReason ? reasonVal : undefined,
      });
      onClose();
    } catch (err) {
            setError(getApiErrorMessage(err, couldNotUpdate('your leave request')));
    } finally {
      setSubmitting(false);
    }
  };

  if (!request) return null;

  return (
    <Dialog open onClose={() => !submitting && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Edit leave request</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {requiresReason && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This leave has already started or includes past dates. Your change will be sent to HR for approval. A reason is required.
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Leave type</InputLabel>
            <Select value={leaveTypeId} label="Leave type" onChange={(e) => setLeaveTypeId(e.target.value)}>
              {leaveTypes.map((t) => (
                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField label="From date" type="date" size="small" fullWidth value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ '& input::-webkit-calendar-picker-indicator': { cursor: 'pointer' } }} />
          <TextField label="To date" type="date" size="small" fullWidth value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ '& input::-webkit-calendar-picker-indicator': { cursor: 'pointer' } }} />
          {from && to && (
            <>
              {from === to ? (
                <FormControl component="fieldset" size="small">
                  <Typography variant="subtitle2" component="legend" gutterBottom>Duration</Typography>
                  <RadioGroup
                    row
                    value={halfDay ? 'half' : 'full'}
                    onChange={(e) => setHalfDay(e.target.value === 'half')}
                  >
                    <FormControlLabel value="full" control={<Radio size="small" />} label="Full day" />
                    <FormControlLabel value="half" control={<Radio size="small" />} label="Half day" />
                  </RadioGroup>
                </FormControl>
              ) : (
                <FormControlLabel
                  control={<Checkbox size="small" checked={halfDay} onChange={(e) => setHalfDay(e.target.checked)} />}
                  label={
                    <Box>
                      <Typography variant="body2" component="span" display="block">
                        This range includes a half day
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                        Subtracts 0.5 from the working-day total.
                      </Typography>
                    </Box>
                  }
                />
              )}
            </>
          )}
          <TextField
            key={`comments-${request.id}`}
            label="Comments"
            size="small"
            fullWidth
            multiline
            rows={2}
            defaultValue={request.comments ?? ''}
            inputRef={(el) => {
              if (el) commentsRef.current = (el as HTMLInputElement).value;
            }}
            onChange={(e) => { commentsRef.current = e.target.value; }}
          />
          {requiresReason && (
            <TextField
              key={`reason-${request.id}`}
              label="Reason for change (required)"
              multiline
              rows={3}
              fullWidth
              required
              defaultValue=""
              inputRef={(el) => {
                if (el) reasonRef.current = (el as HTMLInputElement).value;
              }}
              onChange={(e) => { reasonRef.current = e.target.value; }}
              placeholder="e.g. Correcting dates, change of leave type"
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !leaveTypeId || !from || !to}
        >
          {submitting ? <CircularProgress size={20} /> : requiresReason ? 'Submit for HR approval' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function MyLeavePage() {
  const { user } = useAuth();
  const { showLegacyHelp } = useAgentContext();
  const isAdmin = user?.roles?.includes('Administrator') ?? false;
  const [data, setData] = useState<LeaveReportResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [periodYear, setPeriodYear] = useState<number>(new Date().getFullYear());

  const [applyOpen, setApplyOpen] = useState(false);
  const [leaveTypes, setLeaveTypes] = useState<LeaveTypeDto[]>([]);
  const [applyLeaveTypeId, setApplyLeaveTypeId] = useState<string>('');
  const [applyFrom, setApplyFrom] = useState<string>('');
  const [applyTo, setApplyTo] = useState<string>('');
  const [applyHalfDay, setApplyHalfDay] = useState(false);
  const [applyComments, setApplyComments] = useState<string>('');
  const [entitlementMessage, setEntitlementMessage] = useState<LeaveEntitlementMessageDto | null>(null);
  const [entitlementLoading, setEntitlementLoading] = useState(false);
  const [entitlementError, setEntitlementError] = useState<string>('');
  const [helpExpanded, setHelpExpanded] = useState(false);
  usePageAgentContext({
    pageId: 'my-leave',
    pageTitle: 'Leave',
    visibleSections: ['leave-requests', 'leave-report', 'hr-pending'],
    suggestedPrompts: [
      'How do I apply for leave on this page?',
      'How do I view the comments or notes I submitted on a leave request?',
      'When is evidence required for a leave request?',
      'Explain the difference between draft and submitted leave requests.',
    ],
  });
  const [applySubmitting, setApplySubmitting] = useState(false);
  const [applyError, setApplyError] = useState<string>('');

  const [myRequests, setMyRequests] = useState<LeaveRequestDto[]>([]);
  const [myRequestsLoading, setMyRequestsLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string>('');

  const [applyFiles, setApplyFiles] = useState<File[]>([]);
  const applyFileInputRef = React.useRef<HTMLInputElement>(null);

  const [evidenceDrawerOpen, setEvidenceDrawerOpen] = useState(false);
  const [evidenceDrawerReadOnly, setEvidenceDrawerReadOnly] = useState(false);
  const [evidenceDrawerRequest, setEvidenceDrawerRequest] = useState<LeaveRequestDto | null>(null);
  const [evidenceDocuments, setEvidenceDocuments] = useState<LeaveRequestEvidenceDto[]>([]);
  const [evidenceLoading, setEvidenceLoading] = useState(false);
  const [evidenceUploading, setEvidenceUploading] = useState(false);
  const [evidenceUploadDialogOpen, setEvidenceUploadDialogOpen] = useState(false);
  const [, setEvidenceDeletingId] = useState<string | null>(null);
  const [cancelLeaveRequest, setCancelLeaveRequest] = useState<LeaveRequestDto | null>(null);
  const [withdrawReasonRequest, setWithdrawReasonRequest] = useState<LeaveRequestDto | null>(null);
  const [withdrawReasonText, setWithdrawReasonText] = useState('');
  const [editRequest, setEditRequest] = useState<LeaveRequestDto | null>(null);
  const [pendingHr, setPendingHr] = useState<LeaveRequestDto[]>([]);
  const [pendingHrLoading, setPendingHrLoading] = useState(false);
  const [hrActionId, setHrActionId] = useState<string | null>(null);
  const [hrConfirm, setHrConfirm] = useState<{ request: LeaveRequestDto; approve: boolean } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiService.get<LeaveReportResponse>(
        `${API_ENDPOINTS.LEAVE.MY_REPORT}?periodYear=${periodYear}`
      );
      setData(res.data ?? { entries: [] });
    } catch (err) {
            setError(getApiErrorMessage(err, couldNotLoad('your leave')));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [periodYear]);

  const loadMyRequests = useCallback(async () => {
    setMyRequestsLoading(true);
    setActionError('');
    try {
      const res = await apiService.get<LeaveRequestDto[]>(API_ENDPOINTS.LEAVE_REQUESTS.MY);
      setMyRequests(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
            setActionError(getApiErrorMessage(err, couldNotLoad('your leave requests')));
      setMyRequests([]);
    } finally {
      setMyRequestsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadMyRequests(); }, [loadMyRequests]);

  const loadPendingHr = useCallback(async () => {
    if (!isAdmin) return;
    setPendingHrLoading(true);
    try {
      const res = await apiService.get<LeaveRequestDto[]>(API_ENDPOINTS.LEAVE_REQUESTS.PENDING_FOR_HR);
      setPendingHr(Array.isArray(res.data) ? res.data : []);
    } catch {
      setPendingHr([]);
    } finally {
      setPendingHrLoading(false);
    }
  }, [isAdmin]);
  useEffect(() => { loadPendingHr(); }, [loadPendingHr]);

  useEffect(() => {
    if (!applyOpen && !editRequest) return;
    apiService.get<LeaveTypeDto[]>(API_ENDPOINTS.LEAVE_REQUESTS.LEAVE_TYPES)
      .then((r) => setLeaveTypes(Array.isArray(r.data) ? r.data : []))
      .catch(() => setLeaveTypes([]));
  }, [applyOpen, editRequest]);

  useEffect(() => {
    if (!applyOpen || !applyLeaveTypeId || !applyFrom || !applyTo) {
      setEntitlementMessage(null);
      setEntitlementError('');
      return;
    }
    const fromDate = new Date(applyFrom);
    const toDate = new Date(applyTo);
    if (toDate < fromDate) {
      setEntitlementMessage(null);
      setEntitlementError('');
      return;
    }
    setEntitlementLoading(true);
    setEntitlementError('');
    apiService.get<LeaveEntitlementMessageDto>(
      API_ENDPOINTS.LEAVE_REQUESTS.ENTITLEMENT_MESSAGE(
        applyLeaveTypeId,
        applyFrom,
        applyTo,
        undefined,
        applyHalfDay
      )
    )
      .then((r) => setEntitlementMessage(r.data ?? null))
      .catch((err: AxiosError<{ message?: string }>) => {
        setEntitlementMessage(null);
        setEntitlementError(getApiErrorMessage(err, ''));
      })
      .finally(() => setEntitlementLoading(false));
  }, [applyOpen, applyLeaveTypeId, applyFrom, applyTo, applyHalfDay]);

  const handleOpenApply = () => {
    setApplyError('');
    setEntitlementError('');
    setApplyLeaveTypeId(leaveTypes[0]?.id ?? '');
    setApplyFrom('');
    setApplyTo('');
    setApplyHalfDay(false);
    setApplyComments('');
    setApplyFiles([]);
    setEntitlementMessage(null);
    if (applyFileInputRef.current) applyFileInputRef.current.value = '';
    setApplyOpen(true);
  };

  const uploadFilesToRequest = async (requestId: string, files: File[], description: string) => {
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      if (description.trim()) formData.append('description', description.trim());
      await apiService.post(API_ENDPOINTS.LEAVE_REQUESTS.EVIDENCE_UPLOAD(requestId), formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
  };

  const handleCreateDraft = async () => {
    if (!applyLeaveTypeId || !applyFrom || !applyTo) return;
    setApplySubmitting(true);
    setApplyError('');
    setEntitlementError('');
    try {
      const createRes = await apiService.post<LeaveRequestDto>(API_ENDPOINTS.LEAVE_REQUESTS.BASE, {
        leaveTypeId: applyLeaveTypeId,
        fromDate: applyFrom,
        toDate: applyTo,
        isHalfDay: applyHalfDay,
        comments: applyComments || undefined,
      });
      const created = createRes.data;
      if (created?.id && applyFiles.length > 0) {
        await uploadFilesToRequest(created.id, applyFiles, '');
      }
      await loadMyRequests();
      setApplyOpen(false);
    } catch (err) {
            setApplyError(getApiErrorMessage(err, couldNotCreate('your leave request')));
    } finally {
      setApplySubmitting(false);
    }
  };

  const handleSubmitRequest = async (requestId: string) => {
    setActionLoadingId(requestId);
    setActionError('');
    try {
      await apiService.post(API_ENDPOINTS.LEAVE_REQUESTS.SUBMIT(requestId));
      await loadMyRequests();
      await load();
    } catch (err) {
            setActionError(getApiErrorMessage(err, couldNotSubmit('your leave request')));
    } finally {
      setActionLoadingId(null);
    }
  };

  const today = formatCalendarDateIso(new Date());
  const isApprovedAndPastOrStarted = (r: LeaveRequestDto) =>
    r.status === 'Approved' && (r.fromDate <= today || r.toDate < today);

  const handleWithdrawRequest = async (requestId: string, body?: { reasonForChange?: string }) => {
    setActionLoadingId(requestId);
    setCancelLeaveRequest(null);
    setWithdrawReasonRequest(null);
    setWithdrawReasonText('');
    try {
      await apiService.post<LeaveRequestDto>(
        API_ENDPOINTS.LEAVE_REQUESTS.WITHDRAW(requestId),
        body ?? {}
      );
      await loadMyRequests();
      await load();
    } catch (err) {
            setActionError(getApiErrorMessage(err, couldNotWithdraw('your leave request')));
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleConfirmCancelApprovedLeave = async () => {
    if (withdrawReasonRequest) {
      if (!withdrawReasonText.trim()) return;
      await handleWithdrawRequest(withdrawReasonRequest.id, { reasonForChange: withdrawReasonText.trim() });
      return;
    }
    if (!cancelLeaveRequest) return;
    await handleWithdrawRequest(cancelLeaveRequest.id);
  };

  const handleHrApprove = async (requestId: string, approve: boolean) => {
    setHrActionId(requestId);
    setActionError('');
    try {
      await apiService.post<LeaveRequestDto>(API_ENDPOINTS.LEAVE_REQUESTS.APPROVE_HR(requestId), { approve });
      await loadPendingHr();
      await loadMyRequests();
      await load();
    } catch (err) {
            setActionError(getApiErrorMessage(err, couldNotProcess('HR approval')));
    } finally {
      setHrActionId(null);
    }
  };

  const handleEditSubmit = useCallback(
    async (payload: {
      leaveTypeId: string;
      fromDate: string;
      toDate: string;
      isHalfDay: boolean;
      comments?: string;
      reasonForChange?: string;
    }) => {
      if (!editRequest) return;
      await apiService.put<LeaveRequestDto>(API_ENDPOINTS.LEAVE_REQUESTS.UPDATE(editRequest.id), payload);
      await loadMyRequests();
      await load();
      setEditRequest(null);
    },
    [editRequest, loadMyRequests, load]
  );

  const handleSubmitForApproval = async () => {
    if (!applyLeaveTypeId || !applyFrom || !applyTo) return;
    const evidenceRequired = entitlementMessage?.evidenceRequired ?? false;
    if (evidenceRequired && applyFiles.length === 0) {
      setApplyError('Evidence is required. Please upload at least one document.');
      return;
    }
    setApplySubmitting(true);
    setApplyError('');
    setEntitlementError('');
    try {
      const createRes = await apiService.post<LeaveRequestDto>(API_ENDPOINTS.LEAVE_REQUESTS.BASE, {
        leaveTypeId: applyLeaveTypeId,
        fromDate: applyFrom,
        toDate: applyTo,
        isHalfDay: applyHalfDay,
        comments: applyComments || undefined,
      });
      const created = createRes.data;
      if (created?.id) {
        if (applyFiles.length > 0) {
          await uploadFilesToRequest(created.id, applyFiles, '');
        }
        await apiService.post(API_ENDPOINTS.LEAVE_REQUESTS.SUBMIT(created.id));
      }
      await loadMyRequests();
      await load();
      setApplyOpen(false);
    } catch (err) {
            setApplyError(getApiErrorMessage(err, couldNotSubmit('your leave request')));
    } finally {
      setApplySubmitting(false);
    }
  };

  const loadEvidenceDocuments = useCallback(async (requestId: string) => {
    setEvidenceLoading(true);
    try {
      const res = await apiService.get<LeaveRequestEvidenceDto[]>(API_ENDPOINTS.LEAVE_REQUESTS.EVIDENCE(requestId));
      setEvidenceDocuments(Array.isArray(res.data) ? res.data : []);
    } catch {
      setEvidenceDocuments([]);
    } finally {
      setEvidenceLoading(false);
    }
  }, []);

  const handleOpenEvidenceDrawer = (request: LeaveRequestDto) => {
    setEvidenceDrawerRequest(request);
    setEvidenceDrawerReadOnly(false);
    setEvidenceDrawerOpen(true);
    loadEvidenceDocuments(request.id);
  };

  const handleViewNotes = (request: LeaveRequestDto) => {
    setEvidenceDrawerRequest(request);
    setEvidenceDrawerReadOnly(true);
    setEvidenceDrawerOpen(true);
    loadEvidenceDocuments(request.id);
  };

  const handleOpenSickLeaveEvidenceFromReport = (row: MyLeaveReportEntry) => {
    if (!row.leaveRequestId) return;
    setEvidenceDrawerRequest({
      id: row.leaveRequestId,
      requesterId: '',
      leaveTypeName: row.leaveTypeName,
      fromDate: row.fromDate ?? '',
      toDate: row.toDate ?? '',
      requestedDays: row.daysTaken,
      status: 'Approved',
      approvedWithAdvancedLeave: false,
      createdAtUtc: '',
    });
    setEvidenceDrawerReadOnly(true);
    setEvidenceDrawerOpen(true);
    loadEvidenceDocuments(row.leaveRequestId);
  };

  const handleCloseEvidenceDrawer = () => {
    const wasDraftEditor = !evidenceDrawerReadOnly;
    setEvidenceDrawerOpen(false);
    setEvidenceDrawerReadOnly(false);
    setEvidenceDrawerRequest(null);
    setEvidenceDocuments([]);
    setEvidenceUploadDialogOpen(false);
    if (wasDraftEditor) {
      loadMyRequests();
    }
  };

  const handleEvidenceUpload = async (files: File[], description: string) => {
    if (!evidenceDrawerRequest?.id) return;
    setEvidenceUploading(true);
    try {
      await uploadFilesToRequest(evidenceDrawerRequest.id, files, description);
      await loadEvidenceDocuments(evidenceDrawerRequest.id);
      setEvidenceUploadDialogOpen(false);
    } catch (err) {
            setActionError(getApiErrorMessage(err, documentCopy.upload));
    } finally {
      setEvidenceUploading(false);
    }
  };

  const handleEvidenceView = async (documentId: string, fileName: string, contentType: string) => {
    if (!evidenceDrawerRequest?.id) return;
    try {
      const response = await apiService.get(
        API_ENDPOINTS.LEAVE_REQUESTS.EVIDENCE_DOWNLOAD(evidenceDrawerRequest.id, documentId),
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: contentType || 'application/octet-stream' });
      if (isViewableContentType(contentType)) {
        openBlobInNewTab(blob, contentType);
      } else {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
            setActionError(getApiErrorMessage(err, documentCopy.open));
    }
  };

  const handleEvidenceDownload = async (documentId: string, fileName: string) => {
    if (!evidenceDrawerRequest?.id) return;
    try {
      const response = await apiService.get(
        API_ENDPOINTS.LEAVE_REQUESTS.EVIDENCE_DOWNLOAD(evidenceDrawerRequest.id, documentId),
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
            setActionError(getApiErrorMessage(err, documentCopy.download));
    }
  };

  const handleEvidenceDelete = async (documentId: string) => {
    if (!evidenceDrawerRequest?.id) return;
    setEvidenceDeletingId(documentId);
    try {
      await apiService.delete(API_ENDPOINTS.LEAVE_REQUESTS.EVIDENCE_DELETE(evidenceDrawerRequest.id, documentId));
      await loadEvidenceDocuments(evidenceDrawerRequest.id);
    } catch (err) {
            setActionError(getApiErrorMessage(err, documentCopy.delete));
    } finally {
      setEvidenceDeletingId(null);
    }
  };

  const applyFileAccept = '.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.txt,.csv,.eml,.msg';

  const years = [new Date().getFullYear(), new Date().getFullYear() - 1];
  const entries = data?.entries ?? [];
  const leaveTableEntries = entries.filter((e) => e.daysTaken > 0);

  return (
    <AppLayout>
      <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <EventAvailableIcon sx={{ fontSize: 32, color: 'primary.main', alignSelf: 'flex-start', mt: 0.5 }} />
          <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 0 }}>
            Leave
          </Typography>
        </Box>

        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Period year</InputLabel>
                <Select
                  value={periodYear}
                  label="Period year"
                  onChange={(e) => setPeriodYear(Number(e.target.value))}
                >
                  {years.map((y) => (
                    <MenuItem key={y} value={y}>{y}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenApply}
              >
                Apply for leave
              </Button>
            </Box>
          </CardContent>
        </Card>

        {(error || actionError) && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => {
              setError('');
              setActionError('');
            }}
          >
            {error && actionError ? couldNotLoad('your leave') : error || actionError}
          </Alert>
        )}

        {isAdmin && (
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>Leave changes pending your approval (HR)</Typography>
              {pendingHrLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : pendingHr.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No leave changes awaiting HR approval.</Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Type</TableCell>
                        <TableCell>Requester</TableCell>
                        <TableCell>From</TableCell>
                        <TableCell>To</TableCell>
                        <TableCell>Reason</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pendingHr.map((r) => (
                        <TableRow key={r.id} hover>
                          <TableCell>{r.leaveTypeName ?? r.leaveTypeCode}</TableCell>
                          <TableCell>{r.requesterName}</TableCell>
                          <TableCell>{formatAppDate(r.fromDate)}</TableCell>
                          <TableCell>{formatAppDate(r.toDate)}</TableCell>
                          <TableCell sx={{ maxWidth: 200 }}>{r.reasonForChange ?? '—'}</TableCell>
                          <TableCell align="right">
                            <Button size="small" color="primary" onClick={() => setHrConfirm({ request: r, approve: true })} disabled={hrActionId === r.id}>
                              {hrActionId === r.id ? <CircularProgress size={16} /> : 'Approve'}
                            </Button>
                            <Button size="small" color="secondary" onClick={() => setHrConfirm({ request: r, approve: false })} disabled={hrActionId === r.id}>
                              Reject
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        )}

        {/* My requests */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>My leave requests</Typography>
            {myRequestsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : myRequests.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <EventAvailableIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  You have no leave requests yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Use &quot;Apply for leave&quot; above to submit a new request.
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>From</TableCell>
                      <TableCell>To</TableCell>
                      <TableCell>Days</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Comments</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {myRequests.map((r) => (
                      <TableRow key={r.id} hover>
                        <TableCell>
                          {r.leaveTypeName ?? r.leaveTypeCode}
                          {r.createdByName && (
                            <Typography component="span" variant="caption" display="block" color="text.secondary">
                              Created on your behalf by {r.createdByName}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{formatAppDate(r.fromDate)}</TableCell>
                        <TableCell>{formatAppDate(r.toDate)}</TableCell>
                        <TableCell>{r.requestedDays}</TableCell>
                        <TableCell>
                          <Chip
                            label={r.status === 'PendingSeniorApproval' ? 'Pending senior approval' : r.status === 'PendingHrApproval' ? 'Pending HR approval' : r.status}
                            size="small"
                            color={r.status === 'Approved' ? 'success' : r.status === 'PendingApproval' || r.status === 'PendingSeniorApproval' || r.status === 'PendingHrApproval' ? 'warning' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 220 }}>
                          {r.comments?.trim() ? (
                            <Tooltip title={r.comments.trim()} placement="top-start">
                              <Typography variant="body2" noWrap>
                                {formatLeaveRequestComments(r.comments)}
                              </Typography>
                            </Tooltip>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {r.status === 'Draft' && (
                            <>
                              <Button size="small" startIcon={<EditIcon />} onClick={() => setEditRequest(r)} disabled={actionLoadingId === r.id}>
                                Edit
                              </Button>
                              <Button size="small" startIcon={<AttachFileIcon />} onClick={() => handleOpenEvidenceDrawer(r)}>
                                Add evidence
                              </Button>
                              <Button size="small" onClick={() => handleSubmitRequest(r.id)} disabled={actionLoadingId === r.id}>
                                {actionLoadingId === r.id ? <CircularProgress size={16} /> : 'Submit'}
                              </Button>
                              <Button size="small" color="secondary" onClick={() => handleWithdrawRequest(r.id)} disabled={actionLoadingId === r.id}>
                                Withdraw
                              </Button>
                            </>
                          )}
                          {(r.status === 'PendingApproval' || r.status === 'PendingSeniorApproval') && (
                            <>
                              <Button size="small" startIcon={<EditIcon />} onClick={() => setEditRequest(r)} disabled={actionLoadingId === r.id}>
                                Edit
                              </Button>
                              <Button size="small" startIcon={<AttachFileIcon />} onClick={() => handleViewNotes(r)} disabled={actionLoadingId === r.id}>
                                View notes
                              </Button>
                              <Button size="small" color="secondary" onClick={() => handleWithdrawRequest(r.id)} disabled={actionLoadingId === r.id}>
                                Withdraw
                              </Button>
                            </>
                          )}
                          {r.status === 'PendingHrApproval' && (
                            <Button size="small" startIcon={<AttachFileIcon />} onClick={() => handleViewNotes(r)} disabled={actionLoadingId === r.id}>
                              View notes
                            </Button>
                          )}
                          {r.status === 'Approved' && (
                            <>
                              <Button size="small" startIcon={<EditIcon />} onClick={() => setEditRequest(r)} disabled={actionLoadingId === r.id}>
                                Edit
                              </Button>
                              <Button size="small" startIcon={<AttachFileIcon />} onClick={() => handleViewNotes(r)} disabled={actionLoadingId === r.id}>
                                View notes
                              </Button>
                              <Button size="small" color="secondary" onClick={() => (isApprovedAndPastOrStarted(r) ? setWithdrawReasonRequest(r) : setCancelLeaveRequest(r))} disabled={actionLoadingId === r.id}>
                                {actionLoadingId === r.id ? <CircularProgress size={16} /> : 'Cancel leave'}
                              </Button>
                            </>
                          )}
                          {r.status === 'Withdrawn' && (
                            <Button size="small" startIcon={<AttachFileIcon />} onClick={() => handleViewNotes(r)} disabled={actionLoadingId === r.id}>
                              View notes
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && data && (
          <>
            {entries.some((e) => e.daysTaken > 0) && (
              <>
                <Card variant="outlined" sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      Leave by type (days)
                    </Typography>
                    <LeaveBarChart
                      entries={entries.filter((e) => e.daysTaken > 0).map((e) => ({
                        employeeId: e.employeeId,
                        employeeName: e.employeeName,
                        leaveTypeName: e.leaveTypeName,
                        daysTaken: e.daysTaken,
                      }))}
                      height={Math.max(300, entries.filter((e) => e.daysTaken > 0).length * 28)}
                    />
                  </CardContent>
                </Card>
                <Card variant="outlined" sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      <strong>Period</strong> shows a month (e.g. Dec 2025) for imported leave balance, or a date range for leave approved in this system. When &quot;Statutory entitlement met&quot; is Yes for Annual Leave, your statutory minimum (15 days) has been met for the fiscal year.
                    </Typography>
                    <LeaveDataTable
                      entries={leaveTableEntries}
                      columns={['leaveType', 'period', 'daysTaken', 'statutory', 'evidence']}
                      emptyMessage="No leave taken this period."
                      defaultSortField="daysTaken"
                      defaultSortDirection="desc"
                      onViewSickLeaveEvidence={handleOpenSickLeaveEvidenceFromReport}
                    />
                  </CardContent>
                </Card>
              </>
            )}
            {!entries.some((e) => e.daysTaken > 0) && (
              <Alert severity="info">No leave taken this period yet.</Alert>
            )}
          </>
        )}
      </Box>

      <Dialog open={applyOpen} onClose={() => setApplyOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Apply for leave</DialogTitle>
        <DialogContent>
          {applyError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setApplyError('')}>
              {applyError}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Leave type</InputLabel>
              <Select
                value={applyLeaveTypeId}
                label="Leave type"
                onChange={(e) => setApplyLeaveTypeId(e.target.value)}
              >
                {leaveTypes.map((t) => (
                  <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="From date"
              type="date"
              size="small"
              fullWidth
              value={applyFrom}
              onChange={(e) => setApplyFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ '& input::-webkit-calendar-picker-indicator': { cursor: 'pointer' } }}
            />
            <TextField
              label="To date"
              type="date"
              size="small"
              fullWidth
              value={applyTo}
              onChange={(e) => setApplyTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ '& input::-webkit-calendar-picker-indicator': { cursor: 'pointer' } }}
            />
            {applyFrom && applyTo && applyFrom === applyTo && (
              <FormControl component="fieldset" size="small">
                <Typography variant="subtitle2" component="legend" gutterBottom>Duration</Typography>
                <RadioGroup
                  row
                  value={applyHalfDay ? 'half' : 'full'}
                  onChange={(e) => setApplyHalfDay(e.target.value === 'half')}
                >
                  <FormControlLabel value="full" control={<Radio size="small" />} label="Full day" />
                  <FormControlLabel value="half" control={<Radio size="small" />} label="Half day" />
                </RadioGroup>
              </FormControl>
            )}
            {applyFrom && applyTo && applyFrom !== applyTo && (
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={applyHalfDay}
                    onChange={(e) => setApplyHalfDay(e.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" component="span" display="block">
                      This range includes a half day
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                      Subtracts 0.5 from the working-day total.
                    </Typography>
                  </Box>
                }
              />
            )}
            <TextField
              label="Comments (optional)"
              size="small"
              fullWidth
              multiline
              rows={2}
              value={applyComments}
              onChange={(e) => setApplyComments(e.target.value)}
            />
            <Box>
              <Typography variant="subtitle2" gutterBottom>Supporting documents (optional for most leave types)</Typography>
              {entitlementMessage?.evidenceRequired && entitlementMessage.evidenceRequiredReason && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  {entitlementMessage.evidenceRequiredReason}
                </Alert>
              )}
              <input
                ref={applyFileInputRef}
                type="file"
                accept={applyFileAccept}
                multiple
                onChange={(e) => {
                  const files = e.target.files;
                  if (files?.length) setApplyFiles((prev) => [...prev, ...Array.from(files)]);
                  e.target.value = '';
                }}
                aria-label="Select supporting documents"
                style={{ display: 'none' }}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<AttachFileIcon />}
                onClick={() => applyFileInputRef.current?.click()}
                sx={{ textTransform: 'none', mb: 1 }}
              >
                {applyFiles.length === 0 ? 'Select files' : `Add more files (${applyFiles.length} selected)`}
              </Button>
              {applyFiles.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                  {applyFiles.map((f, i) => (
                    <Chip
                      key={`${f.name}-${i}`}
                      label={f.name}
                      size="small"
                      onDelete={() => setApplyFiles((prev) => prev.filter((_, idx) => idx !== i))}
                      sx={{ maxWidth: 200 }}
                    />
                  ))}
                </Box>
              )}
            </Box>
            {entitlementLoading && <Typography variant="body2" color="text.secondary">Loading…</Typography>}
            {entitlementError && !entitlementLoading && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {entitlementError}
              </Alert>
            )}
            {entitlementMessage && !entitlementLoading && !entitlementError && (
              <Alert severity="info" sx={{ mt: 1 }}>
                {entitlementMessage.capMessage ? (
                  <>{entitlementMessage.capMessage}</>
                ) : entitlementMessage.appliesToThisType ? (
                  <>
                    This request is for {entitlementMessage.requestedDays} day{entitlementMessage.requestedDays !== 1 ? 's' : ''}.
                    You have already taken {entitlementMessage.takenInCycle} day{entitlementMessage.takenInCycle !== 1 ? 's' : ''} this year.
                  </>
                ) : (
                  <>This request is for {entitlementMessage.requestedDays} day{entitlementMessage.requestedDays !== 1 ? 's' : ''}.</>
                )}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplyOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateDraft} disabled={applySubmitting || !applyLeaveTypeId || !applyFrom || !applyTo || !!entitlementError}>
            {applySubmitting ? <CircularProgress size={20} /> : 'Save as draft'}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitForApproval}
            disabled={
              applySubmitting ||
              !applyLeaveTypeId ||
              !applyFrom ||
              !applyTo ||
              !!entitlementError ||
              (!!entitlementMessage?.evidenceRequired && applyFiles.length === 0)
            }
          >
            {applySubmitting ? <CircularProgress size={20} /> : 'Submit for approval'}
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer
        anchor="right"
        open={evidenceDrawerOpen}
        onClose={handleCloseEvidenceDrawer}
        PaperProps={{ sx: { width: { xs: '100%', sm: 420 } } }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            {evidenceDrawerReadOnly
              ? 'Leave notes'
              : `Evidence for ${evidenceDrawerRequest?.leaveTypeName ?? evidenceDrawerRequest?.leaveTypeCode ?? 'leave'}`}
          </Typography>
          {evidenceDrawerRequest && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {formatAppDate(evidenceDrawerRequest.fromDate)} – {formatAppDate(evidenceDrawerRequest.toDate)} ({evidenceDrawerRequest.requestedDays} days)
            </Typography>
          )}
          {evidenceDrawerReadOnly && evidenceDrawerRequest?.comments?.trim() && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Comments</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {evidenceDrawerRequest.comments.trim()}
              </Typography>
            </Box>
          )}
          {actionError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError('')}>
              {actionError}
            </Alert>
          )}
          {!evidenceDrawerReadOnly && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AttachFileIcon />}
              onClick={() => setEvidenceUploadDialogOpen(true)}
              sx={{ mb: 2, textTransform: 'none' }}
            >
              Upload document
            </Button>
          )}
          <Typography variant="subtitle2" gutterBottom>
            Supporting documents
          </Typography>
          {evidenceLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <SupportingDocumentsTable
              documents={evidenceDocuments.map((d) => ({
                id: d.id,
                fileName: d.fileName,
                description: d.description,
                uploadedByName: d.uploadedByName,
                uploadedAt: d.uploadedAt,
                fileSize: d.fileSize,
                contentType: d.contentType,
              }))}
              onView={handleEvidenceView}
              onDownload={handleEvidenceDownload}
              onDelete={evidenceDrawerReadOnly ? () => {} : (id, _fileName) => handleEvidenceDelete(id)}
              canEdit={false}
              formatDate={(date) => formatAppDate(date, { includeTime: true })}
            />
          )}
          {evidenceDocuments.length === 0 && !evidenceLoading && (
            <Typography variant="body2" color="text.secondary">
              {evidenceDrawerReadOnly ? 'No documents were uploaded for this leave request.' : 'No documents uploaded yet.'}
            </Typography>
          )}
          <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
            {!evidenceDrawerReadOnly && (
              <Button variant="contained" onClick={() => evidenceDrawerRequest && handleSubmitRequest(evidenceDrawerRequest.id)} disabled={actionLoadingId === evidenceDrawerRequest?.id}>
                {actionLoadingId === evidenceDrawerRequest?.id ? <CircularProgress size={20} /> : 'Submit for approval'}
              </Button>
            )}
            <Button onClick={handleCloseEvidenceDrawer}>Close</Button>
          </Box>
        </Box>
      </Drawer>

      <DocumentUploadDialog
        open={evidenceUploadDialogOpen}
        onClose={() => setEvidenceUploadDialogOpen(false)}
        onUpload={handleEvidenceUpload}
        uploading={evidenceUploading}
        title="Upload leave evidence"
      />

      <Dialog
        open={!!withdrawReasonRequest}
        onClose={() => { if (!actionLoadingId) { setWithdrawReasonRequest(null); setWithdrawReasonText(''); } }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Withdraw approved leave (reason required)</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            This leave has already started or includes past dates. Withdrawing requires HR approval. Please provide a reason for the change.
          </Typography>
          <TextField
            label="Reason for change"
            multiline
            rows={3}
            fullWidth
            required
            value={withdrawReasonText}
            onChange={(e) => setWithdrawReasonText(e.target.value)}
            placeholder="e.g. Change of plans, cancelled trip"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setWithdrawReasonRequest(null); setWithdrawReasonText(''); }} disabled={!!actionLoadingId}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleConfirmCancelApprovedLeave}
            disabled={!!actionLoadingId || !withdrawReasonText.trim()}
          >
            {actionLoadingId ? <CircularProgress size={20} /> : 'Submit for HR approval'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!hrConfirm}
        onClose={() => !hrActionId && setHrConfirm(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{hrConfirm?.approve ? 'Approve leave change?' : 'Reject leave change?'}</DialogTitle>
        <DialogContent>
          {hrConfirm && (
            <Typography component="div">
              <strong>{hrConfirm.request.requesterName}</strong> — {hrConfirm.request.leaveTypeName ?? hrConfirm.request.leaveTypeCode}: {formatAppDate(hrConfirm.request.fromDate)} – {formatAppDate(hrConfirm.request.toDate)}
              {hrConfirm.request.reasonForChange && (
                <>
                  <br /><Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Reason: {hrConfirm.request.reasonForChange}</Typography>
                </>
              )}
              {!hrConfirm.approve && (
                <Typography variant="body2" sx={{ mt: 1.5 }}>
                  The employee&apos;s original leave will be restored and they will be notified.
                </Typography>
              )}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHrConfirm(null)} disabled={!!hrActionId}>Cancel</Button>
          <Button
            variant="contained"
            color={hrConfirm?.approve ? 'primary' : 'secondary'}
            disabled={!!hrActionId}
            onClick={async () => {
              if (!hrConfirm) return;
              const { request, approve } = hrConfirm;
              await handleHrApprove(request.id, approve);
              setHrConfirm(null);
            }}
          >
            {hrActionId && hrConfirm && hrActionId === hrConfirm.request.id ? <CircularProgress size={20} /> : hrConfirm?.approve ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {editRequest && (
        <EditLeaveDialog
          request={editRequest}
          leaveTypes={leaveTypes}
          onClose={() => setEditRequest(null)}
          onSubmit={handleEditSubmit}
        />
      )}

      <Dialog
        open={!!cancelLeaveRequest}
        onClose={() => !actionLoadingId && setCancelLeaveRequest(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Cancel approved leave</DialogTitle>
        <DialogContent>
          <Typography>
            This leave has been approved. Cancelling will remove these days from your leave taken. Continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelLeaveRequest(null)} disabled={!!actionLoadingId}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleConfirmCancelApprovedLeave}
            disabled={!!actionLoadingId}
          >
            {actionLoadingId ? <CircularProgress size={20} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {showLegacyHelp && (
        <MyLeaveHelpSidebar isExpanded={helpExpanded} onToggle={() => setHelpExpanded(!helpExpanded)} />
      )}
    </AppLayout>
  );
}
